import { Button } from '@cloudscape-design/components';
import { useLocation } from 'react-router-dom';
import { generateShareableUrl } from 'src/utils/urlFilterUtils';
import { createTinyUrl, isTinyUrlSupported } from 'src/utils/tinyUrlUtils';
import { ApiRequestStatus } from 'src/types/ApiRequestStatus';

/**
 * ShareFiltersButton Component
 *
 * Generates a shareable tiny URL with encoded filters and copies to clipboard
 * Uses Amazon's tiny.amazon.com service for short-lived URLs (3 days default)
 * Automatically provides status updates via onStatusChange callback
 */
export interface ShareFiltersButtonProps<T> {
  'filters': T;
  'businessUnitId': string;
  'onStatusChange': (status: ApiRequestStatus[]) => void;
  'useTinyUrl'?: boolean; // Enable tiny URL generation (default: true)
  'tinyUrlTtlDays'?: number; // TTL for tiny URLs in days (default: 3)
  'data-testid'?: string;
}

/**
 * Helper to create success status for ApiStatusFlashBar
 */
export function createShareSuccessStatus(): ApiRequestStatus[] {
  return [
    {
      isSuccess: true,
      successMessage: 'Filter URL copied to clipboard',
      isLoading: false,
      errorMessage: null,
    },
  ];
}

/**
 * Helper to create error status for ApiStatusFlashBar
 */
export function createShareErrorStatus(error: Error): ApiRequestStatus[] {
  return [
    {
      isSuccess: false,
      errorMessage: error.message,
      isLoading: false,
      successMessage: null,
    },
  ];
}

export function ShareFiltersButton<T extends Record<string, any>>({
  filters,
  businessUnitId,
  onStatusChange,
  useTinyUrl = true,
  tinyUrlTtlDays = 3,
  'data-testid': dataTestId,
}: ShareFiltersButtonProps<T>) {
  const location = useLocation();

  const handleShare = async () => {
    try {
      // Generate shareable URL
      const shareableUrl = generateShareableUrl(
        location.pathname,
        filters,
        businessUnitId,
      );

      // Create full URL with origin
      const fullUrl = `${window.location.origin}${shareableUrl}`;

      let urlToShare = fullUrl;

      // Try to create tiny URL if enabled and supported in current environment
      if (useTinyUrl && isTinyUrlSupported()) {
        try {
          const tinyUrlResult = await createTinyUrl(fullUrl, {
            ttlDays: tinyUrlTtlDays,
          });
          urlToShare = tinyUrlResult.tinyUrl;
        } catch (tinyUrlError) {
          // Fallback to full URL if tiny URL creation fails
          console.warn('Tiny URL creation failed, using full URL:', tinyUrlError);
          // Continue with full URL
        }
      }

      // Copy to clipboard
      await navigator.clipboard.writeText(urlToShare);

      // Notify parent with success status
      onStatusChange(createShareSuccessStatus());
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to share filters');

      // Notify parent with error status
      onStatusChange(createShareErrorStatus(err));
    }
  };

  return (
    <Button
      iconName="share"
      onClick={handleShare}
      data-testid={dataTestId}
      variant="normal"
    >
      Share
    </Button>
  );
}
